import React, { useContext } from 'react'
import {
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import "../../../scss/widget.scss"
import { fuzzyFilter } from './TableHelper'
import DebouncedInput from './DebouncedInput'
import TableFilter from './TableFilter'
import { ThemeContext } from '../../../models/ThemeContext'
import { ThemeTypes } from '../../../models/SharedModel'

interface IInfiniteScrollProps {
  display: boolean,
  fetchSize: number,
  handlefetch: Function
}

interface ITableComponentProps {
  columns: any,
  tableData: any,
  infiniteScroll?: IInfiniteScrollProps,
  enableSort?: boolean,
  enableFilter?: boolean,
  enableGlobalFilter?: boolean,
  getRowClassName?: Function;
}

const TableComponent = (props: ITableComponentProps) => {
  const { columns, tableData, infiniteScroll, enableFilter, enableSort, enableGlobalFilter, getRowClassName } = props;

  const tableContainerRef = React.useRef<HTMLDivElement>(null)

  // states
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')

  
  //react-query has an useInfiniteQuery hook just for this situation!
  const { data, fetchNextPage, isFetching, isLoading } =
    useInfiniteQuery({
      queryKey: ['table', tableData],
      queryFn: async ({ pageParam = 0 }) => {
        const size = infiniteScroll ? infiniteScroll?.fetchSize : 25
        const start = pageParam * size
        const fetchedData = infiniteScroll ? infiniteScroll?.handlefetch(start, size, tableData) : []
        return fetchedData
      },
      getNextPageParam: (_lastGroup, groups) => groups.length,
      initialPageParam: 0
    })

  //we must flatten the array of arrays from the useInfiniteQuery hook
  const flatData = React.useMemo(
    () => infiniteScroll?.display ? (
      data?.pages?.flatMap((page: any) => page.data) ?? []
      ) : tableData,
    [data]
  )
  const totalDBRowCount = (data?.pages?.[0] as any)?.meta?.totalRowCount ?? 0
  const totalFetched = flatData.length

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  //a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  React.useEffect(() => {
    infiniteScroll?.display && fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const table = useReactTable({
    data: flatData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    globalFilterFn: fuzzyFilter,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  })
  
  const { themeName } = useContext(ThemeContext);
  return (
    <div className={`table-main p-2 ${themeName === ThemeTypes?.BLUE_THEME ? '' :'table-main-dark' }`}>
      {enableGlobalFilter ? <DebouncedInput
        value={globalFilter ?? ''}
        onChange={value => setGlobalFilter(String(value))}
        className="p-2 font-lg shadow border border-block"
        placeholder="Search all columns..."
      /> : <></>}
      <div className="h-2" />
      <div
        className="table-container"
        onScroll={e => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        ref={tableContainerRef}
      >
        {<div className='fetched'>
          {infiniteScroll?.display ? `Showing ${flatData.length} of ${totalDBRowCount} results.` : ''}
        </div>}
        <table>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header  => {
                  return (
                    <th key={header.id} colSpan={header.colSpan} className={header?.column?.id}>
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: enableSort ? header.column.getToggleSortingHandler() : () => {},
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                          {header.column.getCanFilter() && enableFilter ? (
                            <div>
                              <TableFilter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row: any) => {
              return (
                <tr className={getRowClassName && getRowClassName(row)}  key={row.id} >
                  {row.getVisibleCells().map((cell: any) => {
                    return (
                      <td key={cell.id} className={cell.column?.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TableComponent